/**
 * Oscilloscope XY Visualizer - Real-time waveform display (Canvas 2D only)
 * Pure Canvas 2D rendering - no WebGL
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';

export class OscilloscopeXYVisualizer extends BaseVisualizer {
  readonly metadata: VisualizerMetadata = {
    id: 'oscilloscope-xy',
    name: 'Oscilloscope XY',
    description: 'Classic oscilloscope with left/right channel XY plot (Canvas 2D)',
    category: '2d',
    defaultZoom: 1,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: false,
    supportsZooming: false,
  };

  render(config: VisualizerConfig): void {
    const { ctx, width, height, waveformData, activeWaves, speed, time } = config;
    const cx = width / 2;
    const cy = height / 2;

    // Enhanced Phosphor fade - more gradual decay for realistic CRT persistence
    ctx.fillStyle = 'rgba(0, 0, 0, 1)'; // Very fast decay
    ctx.fillRect(0, 0, width, height);
    
    // Add subtle green tint to background for classic CRT look
    ctx.fillStyle = 'rgba(0, 20, 10, 0.01)';
    ctx.fillRect(0, 0, width, height);
    
    // Subtle crosshairs
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx, 0);
    ctx.lineTo(cx, height);
    ctx.moveTo(0, cy);
    ctx.lineTo(width, cy);
    ctx.stroke();

    const margin = 20;
    const radiusX = (width / 2) - margin;
    const radiusY = (height / 2) - margin;

    // === TRUE OSCILLOSCOPE MODE: Use raw waveform data when available ===
    if (waveformData && waveformData.left.length > 0) {
      const leftSamples = waveformData.left;
      const rightSamples = waveformData.right;
      const numSamples = Math.min(leftSamples.length, rightSamples.length);
      
      // Simple single-pass rendering with interpolation for higher visual resolution
      ctx.fillStyle = 'rgba(200, 255, 200, 0.95)';
      ctx.shadowBlur = 3;
      ctx.shadowColor = '#00FF00';
      
      // Interpolate between samples for denser dot coverage
      const interpolationSteps = 3; // Creates intermediate points
      
      for (let i = 0; i < numSamples - 1; i++) {
        const x1 = leftSamples[i];
        const y1 = rightSamples[i];
        const x2 = leftSamples[i + 1];
        const y2 = rightSamples[i + 1];
        
        for (let step = 0; step <= interpolationSteps; step++) {
          const t = step / interpolationSteps;
          const x = x1 + (x2 - x1) * t;
          const y = y1 + (y2 - y1) * t;
          
          const plotX = cx + (x * radiusX);
          const plotY = cy - (y * radiusY);
          
          ctx.fillRect(plotX - 0.5, plotY - 0.5, 1, 1);
        }
      }
    } else if (activeWaves.length > 0) {
      // === FALLBACK MODE: Generate from frequency layers when no music playing ===
      // Organize waves by channel to create proper stereo XY plot
      const leftWaves = activeWaves.filter(w => w.channel === 'left');
      const rightWaves = activeWaves.filter(w => w.channel === 'right');
      const bothWaves = activeWaves.filter(w => (w.channel || 'both') === 'both');
      
      // Use left channel waves for X, right channel waves for Y
      // If only one channel exists, the other axis will be zero (normal oscilloscope behavior)
      const xWaves = [...leftWaves, ...bothWaves];
      const yWaves = [...rightWaves, ...bothWaves];
      
      const numSamples = 1024;
      
      ctx.fillStyle = 'rgba(200, 255, 200, 0.95)';
      ctx.shadowBlur = 3;
      ctx.shadowColor = '#00FF00';
      
      const interpolationSteps = 3;
      
      for (let i = 0; i < numSamples - 1; i++) {
        const t1 = time + (i / numSamples) * 0.05;
        const t2 = time + ((i + 1) / numSamples) * 0.05;
        
        // Compute X from X-channel waves (or 0 if no waves)
        let xVal1 = 0, xVal2 = 0;
        if (xWaves.length > 0) {
          let xAmpSum = 0;
          xWaves.forEach(w => {
            const wPhase = (w.phase * Math.PI) / 180;
            const effectiveFreq = w.freq * speed;
            xVal1 += (w.amp / 100) * this.sampleWaveform(w.waveform, 2 * Math.PI * effectiveFreq * t1 + wPhase);
            xVal2 += (w.amp / 100) * this.sampleWaveform(w.waveform, 2 * Math.PI * effectiveFreq * t2 + wPhase);
            xAmpSum += (w.amp / 100);
          });
          xVal1 = xAmpSum > 0 ? xVal1 / xAmpSum : 0;
          xVal2 = xAmpSum > 0 ? xVal2 / xAmpSum : 0;
        }
        
        // Compute Y from Y-channel waves (or 0 if no waves)
        let yVal1 = 0, yVal2 = 0;
        if (yWaves.length > 0) {
          let yAmpSum = 0;
          yWaves.forEach(w => {
            const wPhase = (w.phase * Math.PI) / 180;
            const effectiveFreq = w.freq * speed;
            yVal1 += (w.amp / 100) * this.sampleWaveform(w.waveform, 2 * Math.PI * effectiveFreq * t1 + wPhase);
            yVal2 += (w.amp / 100) * this.sampleWaveform(w.waveform, 2 * Math.PI * effectiveFreq * t2 + wPhase);
            yAmpSum += (w.amp / 100);
          });
          yVal1 = yAmpSum > 0 ? yVal1 / yAmpSum : 0;
          yVal2 = yAmpSum > 0 ? yVal2 / yAmpSum : 0;
        }
        
        for (let step = 0; step <= interpolationSteps; step++) {
          const t = step / interpolationSteps;
          const x = xVal1 + (xVal2 - xVal1) * t;
          const y = yVal1 + (yVal2 - yVal1) * t;
          
          const plotX = cx + (x * radiusX);
          const plotY = cy - (y * radiusY);
          
          ctx.fillRect(plotX - 0.5, plotY - 0.5, 1, 1);
        }
      }
    } else {
      // No data and not enough frequency layers - show idle message
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.font = '14px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('No audio signal', cx, cy);
    }
    
    ctx.shadowBlur = 0;
  }
}
